import { createContext, useContext, useLayoutEffect, ReactNode } from 'react';
import useDemoMode from '../hooks/useDemoMode';
import useMockDataStore from '../hooks/useMockDataStore';
import { demoModeManager } from '../services/demoModeManager';
import type { UserMode } from '../types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type MockDataStore = ReturnType<typeof useMockDataStore>;

export interface DemoModeContextValue {
  isDemoMode: boolean;
  mode: UserMode;
  isAnonymous: boolean;
  isOnboarding: boolean;
  isSubscribed: boolean;
  mockDataStore: MockDataStore;
}

const DemoModeContext = createContext<DemoModeContextValue | null>(null);

export const DemoModeProvider = ({ children }: { children: ReactNode }) => {
  const demoMode = useDemoMode();
  const mockDataStore = useMockDataStore();
  const mode = demoMode.mode;

  useLayoutEffect(() => {
    demoModeManager.setDemoMode(demoMode.isDemoMode, mode, mockDataStore);
  }, [demoMode.isDemoMode, mode, mockDataStore]);

  const value: DemoModeContextValue = {
    isDemoMode: demoMode.isDemoMode,
    mode,
    isAnonymous: demoMode.isAnonymous,
    isOnboarding: demoMode.isOnboarding,
    isSubscribed: demoMode.isSubscribed,
    mockDataStore
  };

  return (
    <DemoModeContext.Provider value={value}>
      {children}
    </DemoModeContext.Provider>
  );
};

export const useDemoModeContext = (): DemoModeContextValue => {
  const context = useContext(DemoModeContext);
  if (!context) {
    throw new Error('useDemoModeContext must be used within a DemoModeProvider');
  }
  return context;
};

export default DemoModeContext;
